import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { MAX_BODY_LENGTH } from '@sokdaq/protocol';
import { useIsTouch } from '../hooks/useTouch.js';
import { useChatStore } from '../store/chat.js';

/** 입력을 멈춘 뒤 타이핑 표시를 끄기까지의 시간. */
const TYPING_IDLE_MS = 2_500;

/**
 * 메시지 입력창.
 *
 * Enter로 보내고 Shift+Enter로 줄바꿈한다. 채팅 앱의 관습을 그대로 따른다.
 * 속닥은 토글이나 Ctrl+Enter로 보낸다.
 *
 * 터치에서는 Enter로 보내지 않는다. 모바일 키보드의 리턴 키는 줄바꿈이고,
 * 보내기는 버튼이 맡는 게 관습이다 — 여기서 가로채면 여러 줄을 쓸 방법이
 * 아예 없어진다.
 */
export function Composer() {
  const isTouch = useIsTouch();
  const status = useChatStore((s) => s.status);
  const sendText = useChatStore((s) => s.sendText);
  const sendWhisper = useChatStore((s) => s.sendWhisper);
  const setTyping = useChatStore((s) => s.setTyping);

  const [body, setBody] = useState('');
  const [whisperMode, setWhisperMode] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const typingSentAt = useRef(0);
  const idleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const disabled = status !== 'open';

  // 컴포넌트가 사라질 때 타이핑 표시를 남기지 않는다.
  useEffect(() => {
    return () => {
      if (idleTimer.current !== null) clearTimeout(idleTimer.current);
    };
  }, []);

  function notifyTyping() {
    const now = Date.now();
    // 글자마다 프레임을 보내지 않는다. 서버 레이트 리밋에도 걸린다.
    if (now - typingSentAt.current > TYPING_IDLE_MS) {
      typingSentAt.current = now;
      setTyping(true);
    }

    if (idleTimer.current !== null) clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(() => {
      typingSentAt.current = 0;
      setTyping(false);
    }, TYPING_IDLE_MS);
  }

  function submit(asWhisper: boolean) {
    const trimmed = body.trim();
    if (trimmed === '' || disabled) return;

    if (asWhisper) sendWhisper(trimmed);
    else sendText(trimmed);

    setBody('');
    if (idleTimer.current !== null) clearTimeout(idleTimer.current);
    typingSentAt.current = 0;
    setTyping(false);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key !== 'Enter') return;
    // Shift+Enter는 줄바꿈. 여기서 가로채면 여러 줄을 쓸 수 없다.
    if (event.shiftKey) return;
    // 터치에서는 리턴 키가 줄바꿈이다. 보내기는 버튼으로.
    if (isTouch) return;

    /*
     * 조합 중인 글자를 확정하는 Enter는 전송이 아니다.
     *
     * 한글·일본어·중국어 입력기는 Enter로 조합을 마무리한다. 그걸 전송으로
     * 읽으면 마지막 글자를 치자마자 반쪽짜리 문장이 날아간다. 모바일
     * 입력기에서 특히 자주 걸린다.
     */
    if (event.nativeEvent.isComposing) return;

    event.preventDefault();
    submit(whisperMode || event.ctrlKey || event.metaKey);
  }

  return (
    <div className="border-t border-neutral-800 p-3">
      <div
        className={`flex items-end gap-2 rounded-lg border bg-neutral-900 px-3 py-2 ${
          whisperMode ? 'border-violet-600/70' : 'border-neutral-800'
        }`}
      >
        <button
          type="button"
          onClick={() => {
            setWhisperMode((on) => !on);
            textareaRef.current?.focus();
          }}
          aria-pressed={whisperMode}
          title="속닥 모드 (Ctrl+Enter로 한 번만 보내기)"
          className={`tap shrink-0 rounded px-2 py-1 text-xs ${
            whisperMode
              ? 'bg-violet-600 text-white'
              : 'bg-neutral-800 text-neutral-400 hover:text-neutral-200'
          }`}
        >
          속닥
        </button>

        <textarea
          ref={textareaRef}
          value={body}
          onChange={(e) => {
            setBody(e.target.value);
            notifyTyping();
          }}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          rows={1}
          maxLength={MAX_BODY_LENGTH}
          placeholder={
            disabled ? '연결을 기다리는 중…' : whisperMode ? '가까운 사람에게만 속닥…' : '메시지를 입력하세요'
          }
          className="max-h-32 min-h-6 flex-1 resize-none bg-transparent text-sm outline-none placeholder:text-neutral-600 disabled:cursor-not-allowed"
        />

        <button
          type="button"
          onClick={() => submit(whisperMode)}
          disabled={disabled || body.trim() === ''}
          className="tap shrink-0 rounded bg-neutral-100 px-3 py-1 text-xs font-medium text-neutral-900 hover:bg-white disabled:opacity-40"
        >
          보내기
        </button>
      </div>

      {!isTouch && (
        <p className="mt-1 px-1 text-[10px] text-neutral-600">
          Enter 전송 · Shift+Enter 줄바꿈 · Ctrl+Enter 속닥
        </p>
      )}
    </div>
  );
}
